import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ClipboardList, Plus, Search, Loader2, Download, Trash2, Edit3,
  TrendingUp, AlertTriangle, Award, CheckCircle, XCircle,
} from "lucide-react";
import {
  FALLBACK_SCM_VENDORS, FALLBACK_RFPS, FALLBACK_BIDS, FALLBACK_CONTRACTS,
  FALLBACK_SCM_AUDIT, FALLBACK_SPEND_DATA, FALLBACK_TOP_VENDORS,
  FALLBACK_EXPIRING_CONTRACTS, FALLBACK_VENDOR_RISKS,
} from "@/data/dummyData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// ── Helper Components ────────────────────────────────────────────────────────

function MiniStatCard({ label, value, color }) {
  return (
    <div className={`bg-white border-l-4 ${color} p-4`}>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function BarChart({ data }) {
  if (!data?.length) return <p className="text-sm text-slate-400 py-4">No data yet.</p>;
  const max = Math.max(...data.map((d) => d.total));
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.month} className="flex items-center gap-3">
          <span className="text-xs text-slate-500 w-16 text-right">{d.month}</span>
          <div className="flex-1 bg-slate-100 h-6">
            <div className="bg-amber-500 h-full" style={{ width: `${(d.total / max) * 100}%` }} />
          </div>
          <span className="text-xs font-mono text-slate-600 w-20">R{d.total?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function LogisticsSCM() {
  const [activeTab, setActiveTab] = useState("dashboard");

  // Data
  const [vendors, setVendors] = useState([]);
  const [rfps, setRfps] = useState([]);
  const [bids, setBids] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [spendData, setSpendData] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [riskVendors, setRiskVendors] = useState([]);

  // UI state
  const [loading, setLoading] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(""); // vendor|rfp|bid|contract
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [awardRfpId, setAwardRfpId] = useState(null);
  const [evalBidId, setEvalBidId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [vendorForm, setVendorForm] = useState({ company_name: "", tax_number: "", contact_person: "", email: "", performance_rating: "3", risk_flags: "" });
  const [rfpForm, setRfpForm] = useState({ requisitioner_name: "", requisitioner_appointment: "", description: "", estimated_value: "", status: "Draft" });
  const [bidForm, setBidForm] = useState({ rfp_id: "", vendor_name: "", bid_amount: "", compliance_docs: "" });
  const [contractForm, setContractForm] = useState({ rfp_id: "", vendor_id: "", vendor_name: "", start_date: "", end_date: "", total_value: "", deliverables: "", compliance_status: "Active" });

  // ── Data Fetching ──────────────────────────────────────────────────────────

  const setLoad = (key, val) => setLoading((p) => ({ ...p, [key]: val }));

  const fetchVendors = useCallback(async () => {
    setLoad("vendors", true); try { const r = await axios.get(`${API}/logistics/scm/vendors`, { headers: getHeaders() }); setVendors(r.data.length > 0 ? r.data : FALLBACK_SCM_VENDORS); } catch { setVendors(FALLBACK_SCM_VENDORS); } finally { setLoad("vendors", false); }
  }, []);
  const fetchRfps = useCallback(async () => {
    setLoad("rfp", true); try { const r = await axios.get(`${API}/logistics/scm/rfp`, { headers: getHeaders() }); setRfps(r.data.length > 0 ? r.data : FALLBACK_RFPS); } catch { setRfps(FALLBACK_RFPS); } finally { setLoad("rfp", false); }
  }, []);
  const fetchBids = useCallback(async () => {
    setLoad("bids", true); try { const r = await axios.get(`${API}/logistics/scm/bids`, { headers: getHeaders() }); setBids(r.data.length > 0 ? r.data : FALLBACK_BIDS); } catch { setBids(FALLBACK_BIDS); } finally { setLoad("bids", false); }
  }, []);
  const fetchContracts = useCallback(async () => {
    setLoad("contracts", true); try { const r = await axios.get(`${API}/logistics/scm/contracts`, { headers: getHeaders() }); setContracts(r.data.length > 0 ? r.data : FALLBACK_CONTRACTS); } catch { setContracts(FALLBACK_CONTRACTS); } finally { setLoad("contracts", false); }
  }, []);
  const fetchAudit = useCallback(async () => {
    try { const r = await axios.get(`${API}/logistics/scm/audit`, { headers: getHeaders() }); setAuditLog(r.data.length > 0 ? r.data : FALLBACK_SCM_AUDIT); } catch { setAuditLog(FALLBACK_SCM_AUDIT); }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [s, tv, e, rv] = await Promise.all([
        axios.get(`${API}/logistics/scm/analytics/spend-by-month`, { headers: getHeaders() }),
        axios.get(`${API}/logistics/scm/analytics/top-vendors`, { headers: getHeaders() }),
        axios.get(`${API}/logistics/scm/analytics/expiring-contracts`, { headers: getHeaders() }),
        axios.get(`${API}/logistics/scm/analytics/risk-flags`, { headers: getHeaders() }),
      ]);
      setSpendData(s.data.length > 0 ? s.data : FALLBACK_SPEND_DATA);
      setTopVendors(tv.data.length > 0 ? tv.data : FALLBACK_TOP_VENDORS);
      setExpiring(e.data.length > 0 ? e.data : FALLBACK_EXPIRING_CONTRACTS);
      setRiskVendors(rv.data.length > 0 ? rv.data : FALLBACK_VENDOR_RISKS);
    } catch {
      setSpendData(FALLBACK_SPEND_DATA);
      setTopVendors(FALLBACK_TOP_VENDORS);
      setExpiring(FALLBACK_EXPIRING_CONTRACTS);
      setRiskVendors(FALLBACK_VENDOR_RISKS);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchVendors(); fetchRfps(); fetchBids(); fetchContracts(); fetchAudit(); fetchAnalytics();
  }, [fetchVendors, fetchRfps, fetchBids, fetchContracts, fetchAudit, fetchAnalytics]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // ── Dialog Helpers ─────────────────────────────────────────────────────────

  const openDialog = (type, editItem = null) => {
    setDialogType(type); setEditId(editItem?.id || null);
    const empty = {
      vendor: () => setVendorForm({ company_name: "", tax_number: "", contact_person: "", email: "", performance_rating: "3", risk_flags: "" }),
      rfp: () => setRfpForm({ requisitioner_name: "", requisitioner_appointment: "", description: "", estimated_value: "", status: "Draft" }),
      bid: () => setBidForm({ rfp_id: "", vendor_name: "", bid_amount: "", compliance_docs: "" }),
      contract: () => setContractForm({ rfp_id: "", vendor_id: "", vendor_name: "", start_date: "", end_date: "", total_value: "", deliverables: "", compliance_status: "Active" }),
    };
    empty[type]?.();
    if (editItem) {
      if (type === "vendor") setVendorForm({ company_name: editItem.company_name || "", tax_number: editItem.tax_number || "", contact_person: editItem.contact_person || "", email: editItem.email || "", performance_rating: editItem.performance_rating?.toString() || "3", risk_flags: editItem.risk_flags || "" });
      if (type === "rfp") setRfpForm({ requisitioner_name: editItem.requisitioner_name || "", requisitioner_appointment: editItem.requisitioner_appointment || "", description: editItem.description || "", estimated_value: editItem.estimated_value?.toString() || "", status: editItem.status || "Draft" });
      if (type === "contract") setContractForm({ rfp_id: editItem.rfp_id || "", vendor_id: editItem.vendor_id || "", vendor_name: editItem.vendor_name || "", start_date: editItem.start_date?.slice(0, 10) || "", end_date: editItem.end_date?.slice(0, 10) || "", total_value: editItem.total_value?.toString() || "", deliverables: editItem.deliverables || "", compliance_status: editItem.compliance_status || "Active" });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let url, payload, successMsg;
      if (dialogType === "vendor") {
        payload = { ...vendorForm, performance_rating: parseInt(vendorForm.performance_rating) || 3, estimated_value: undefined };
        url = editId ? `${API}/logistics/scm/vendors/${editId}` : `${API}/logistics/scm/vendors`;
        successMsg = editId ? "Vendor updated" : "Vendor created";
        if (editId) { await axios.put(url, payload, { headers: getHeaders() }); } else { await axios.post(url, payload, { headers: getHeaders() }); }
      } else if (dialogType === "rfp") {
        payload = { ...rfpForm, estimated_value: parseFloat(rfpForm.estimated_value) || 0 };
        url = editId ? `${API}/logistics/scm/rfp/${editId}` : `${API}/logistics/scm/rfp`;
        successMsg = editId ? "RFP updated" : "RFP created";
        if (editId) { await axios.put(url, payload, { headers: getHeaders() }); } else { await axios.post(url, payload, { headers: getHeaders() }); }
      } else if (dialogType === "bid") {
        payload = { rfp_id: bidForm.rfp_id, vendor_name: bidForm.vendor_name, bid_amount: parseFloat(bidForm.bid_amount) || 0, compliance_docs: bidForm.compliance_docs ? bidForm.compliance_docs.split(",").map((s) => s.trim()) : [] };
        await axios.post(`${API}/logistics/scm/bids`, payload, { headers: getHeaders() });
        successMsg = "Bid submitted";
      } else if (dialogType === "contract") {
        payload = { ...contractForm, total_value: parseFloat(contractForm.total_value) || 0 };
        url = editId ? `${API}/logistics/scm/contracts/${editId}` : `${API}/logistics/scm/contracts`;
        successMsg = editId ? "Contract updated" : "Contract created";
        if (editId) { await axios.put(url, payload, { headers: getHeaders() }); } else { await axios.post(url, payload, { headers: getHeaders() }); }
      }
      toast.success(successMsg);
      setDialogOpen(false);
      refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      let url;
      if (deleteId.type === "vendor") url = `${API}/logistics/scm/vendors/${deleteId.id}`;
      else if (deleteId.type === "rfp") url = `${API}/logistics/scm/rfp/${deleteId.id}`;
      else if (deleteId.type === "contract") url = `${API}/logistics/scm/contracts/${deleteId.id}`;
      else return;
      await axios.delete(url, { headers: getHeaders() });
      toast.success("Deleted");
      setDeleteId(null);
      refreshAll();
    } catch { toast.error("Failed to delete"); }
  };

  const handleEvaluate = async (bidId, status) => {
    try {
      await axios.post(`${API}/logistics/scm/bids/${bidId}/evaluate`, { status }, { headers: getHeaders() });
      toast.success(`Bid marked as ${status}`);
      fetchBids();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to evaluate"); }
  };

  const handleAward = async () => {
    if (!awardRfpId) return;
    try {
      await axios.post(`${API}/logistics/scm/rfp/${awardRfpId}/award`, {}, { headers: getHeaders() });
      toast.success("RFP awarded! Contract created.");
      setAwardRfpId(null);
      refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Award failed"); }
  };

  const handleExport = async (type) => {
    try {
      const res = await axios.get(`${API}/logistics/scm/export/${type}`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `logistics_${type}.csv`; a.click();
    } catch { toast.error("Export failed"); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const getRfpBids = (rfpId) => bids.filter((b) => b.rfp_id === rfpId);
  const getWinner = (rfpId) => bids.find((b) => b.rfp_id === rfpId && b.status === "Winner");

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">SUPPLY CHAIN</h1>
            <p className="text-slate-600">Procurement, vendors & contract management</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="rounded-none border border-slate-200 mb-6 flex-wrap">
          {["dashboard", "vendors", "rfp", "contracts", "audit"].map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-none data-[state=active]:bg-amber-600 data-[state=active]:text-white capitalize">
              {t === "rfp" ? "RFP / Bids" : t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── Dashboard Tab ──────────────────────────────────────────────── */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-4 gap-4 mb-6">
            <MiniStatCard label="Total Vendors" value={vendors.length} color="border-blue-500" />
            <MiniStatCard label="Active RFPs" value={rfps.filter((r) => r.status === "Open for Bids").length} color="border-amber-500" />
            <MiniStatCard label="Active Contracts" value={contracts.filter((c) => c.compliance_status === "Active").length} color="border-green-500" />
            <MiniStatCard label="Risk Flags" value={riskVendors.length} color="border-red-500" />
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Monthly Spend</h3>
              <BarChart data={spendData} />
            </div>
            <div className="bg-white border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-700 mb-3">Top Vendors by Spend</h3>
              {topVendors.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
                <div className="space-y-2">
                  {topVendors.slice(0, 5).map((v, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{v.vendor_name || "Unknown"}</span>
                      <span className="font-mono text-slate-500">R{v.total_spend?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Expiring Contracts (30 days)</h3>
              {expiring.length === 0 ? <p className="text-sm text-slate-400">No contracts expiring soon.</p> : (
                <div className="space-y-2">
                  {expiring.map((c) => (
                    <div key={c.id} className="bg-amber-50 p-2 text-sm">
                      <span className="font-medium">{c.vendor_name}</span>
                      <span className="text-xs text-slate-500 ml-2">ends {c.end_date?.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Vendors with Risk Flags</h3>
              {riskVendors.length === 0 ? <p className="text-sm text-slate-400">No risk flags.</p> : (
                <div className="space-y-2">
                  {riskVendors.map((v) => (
                    <div key={v.id} className="bg-red-50 p-2 text-sm">
                      <span className="font-medium">{v.company_name}</span>
                      <p className="text-xs text-red-600 mt-1">{v.risk_flags}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Vendors Tab ────────────────────────────────────────────────── */}
        <TabsContent value="vendors">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">Vendors</h2>
            <div className="flex gap-2">
              <Button onClick={() => openDialog("vendor")} className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-none text-white"><Plus className="w-4 h-4 mr-1" /> Add Vendor</Button>
              <Button onClick={() => handleExport("vendors")} variant="outline" className="rounded-none border-slate-300"><Download className="w-4 h-4 mr-1" /> CSV</Button>
            </div>
          </div>
          {loading.vendors ? <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto my-8" /> : (
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-app border-b">
                  <tr>{["Company", "Contact", "Email", "Rating", "Risk Flags", ""].map((h) => <th key={h} className="text-left p-3 font-semibold text-slate-600">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {vendors.map((v) => (
                    <tr key={v.id} className="border-b border-slate-100 hover:bg-app">
                      <td className="p-3 font-medium">{v.company_name}</td>
                      <td className="p-3 text-xs text-slate-600">{v.contact_person || "-"}</td>
                      <td className="p-3 text-xs text-slate-500">{v.email || "-"}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 ${(v.performance_rating || 0) <= 2 ? "bg-red-100 text-red-700" : (v.performance_rating || 0) >= 4 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {v.performance_rating || 3}/5
                        </span>
                      </td>
                      <td className="p-3">{v.risk_flags ? <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5">{v.risk_flags}</span> : <span className="text-xs text-slate-400">-</span>}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button onClick={() => openDialog("vendor", v)} className="p-1 text-slate-400 hover:text-amber-600"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => setDeleteId({ id: v.id, type: "vendor" })} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ── RFP / Bids Tab ─────────────────────────────────────────────── */}
        <TabsContent value="rfp">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">Procurement Requests (RFP)</h2>
            <div className="flex gap-2">
              <Button onClick={() => openDialog("bid")} variant="outline" className="rounded-none border-slate-300"><Plus className="w-4 h-4 mr-1" /> Add Bid</Button>
              <Button onClick={() => openDialog("rfp")} className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-none text-white"><Plus className="w-4 h-4 mr-1" /> New RFP</Button>
              <Button onClick={() => handleExport("rfp")} variant="outline" className="rounded-none border-slate-300"><Download className="w-4 h-4 mr-1" /> CSV</Button>
            </div>
          </div>
          {loading.rfp ? <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto my-8" /> : (
            <div className="space-y-4">
              {rfps.map((rfp) => {
                const rfpBids = getRfpBids(rfp.id);
                const winner = getWinner(rfp.id);
                const statusColor = rfp.status === "Awarded" ? "bg-green-100 text-green-700" : rfp.status === "Open for Bids" ? "bg-blue-100 text-blue-700" : rfp.status === "Closed" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700";
                return (
                  <div key={rfp.id} className="bg-white border border-slate-200 p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-400">{rfp.id}</span>
                          <span className={`text-xs px-2 py-0.5 font-medium ${statusColor}`}>{rfp.status}</span>
                        </div>
                        <p className="font-medium text-slate-800 mt-1">{rfp.description?.slice(0, 100)}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {rfp.requisitioner_name} ({rfp.requisitioner_appointment}) • R{rfp.estimated_value?.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openDialog("rfp", rfp)} className="p-1 text-slate-400 hover:text-amber-600"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteId({ id: rfp.id, type: "rfp" })} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    {/* Bids for this RFP */}
                    {rfpBids.length > 0 && (
                      <div className="ml-6 mt-3 border-l-2 border-slate-200 pl-4 space-y-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase">Bids</p>
                        {rfpBids.map((bid) => (
                          <div key={bid.id} className="flex items-center justify-between bg-app p-2">
                            <div>
                              <span className="text-sm font-medium">{bid.vendor_name}</span>
                              <span className="text-xs text-slate-500 ml-2">R{bid.bid_amount?.toLocaleString()}</span>
                              <span className={`text-xs ml-2 px-1.5 py-0.5 ${bid.status === "Winner" ? "bg-green-100 text-green-700" : bid.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                                {bid.status}
                              </span>
                            </div>
                            <div className="flex gap-1">
                              {bid.status === "Received" && (
                                <>
                                  <button onClick={() => handleEvaluate(bid.id, "Winner")} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Mark Winner"><CheckCircle className="w-4 h-4" /></button>
                                  <button onClick={() => handleEvaluate(bid.id, "Rejected")} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Reject"><XCircle className="w-4 h-4" /></button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        {winner && rfp.status !== "Awarded" && (
                          <button onClick={() => setAwardRfpId(rfp.id)} className="text-xs bg-green-600 hover:bg-green-500 text-white px-3 py-1 flex items-center gap-1">
                            <Award className="w-3 h-3" /> Award Contract
                          </button>
                        )}
                      </div>
                    )}
                    {rfpBids.length === 0 && (
                      <p className="ml-6 text-xs text-slate-400 italic mt-2">No bids yet.</p>
                    )}
                  </div>
                );
              })}
              {rfps.length === 0 && <p className="text-slate-500 text-center py-8">No RFPs found.</p>}
            </div>
          )}
        </TabsContent>

        {/* ── Contracts Tab ──────────────────────────────────────────────── */}
        <TabsContent value="contracts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">Contracts</h2>
            <div className="flex gap-2">
              <Button onClick={() => openDialog("contract")} className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-none text-white"><Plus className="w-4 h-4 mr-1" /> Add Contract</Button>
              <Button onClick={() => handleExport("contracts")} variant="outline" className="rounded-none border-slate-300"><Download className="w-4 h-4 mr-1" /> CSV</Button>
            </div>
          </div>
          {loading.contracts ? <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto my-8" /> : (
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-app border-b">
                  <tr>{["Vendor", "RFP", "Value", "Start", "End", "Status", ""].map((h) => <th key={h} className="text-left p-3 font-semibold text-slate-600">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-app">
                      <td className="p-3 font-medium">{c.vendor_name}</td>
                      <td className="p-3 text-xs font-mono text-slate-500">{c.rfp_id}</td>
                      <td className="p-3 font-mono text-xs">R{c.total_value?.toLocaleString()}</td>
                      <td className="p-3 text-xs text-slate-500">{c.start_date?.slice(0, 10)}</td>
                      <td className="p-3 text-xs text-slate-500">{c.end_date?.slice(0, 10)}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 font-medium ${c.compliance_status === "Active" ? "bg-green-100 text-green-700" : c.compliance_status === "Expired" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {c.compliance_status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button onClick={() => openDialog("contract", c)} className="p-1 text-slate-400 hover:text-amber-600"><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => setDeleteId({ id: c.id, type: "contract" })} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contracts.length === 0 && <p className="text-center py-8 text-slate-500">No contracts found.</p>}
            </div>
          )}
        </TabsContent>

        {/* ── Audit Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="audit">
          <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800 mb-4">Audit Log</h2>
          {auditLog.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No audit entries yet.</p>
          ) : (
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-app border-b">
                  <tr>{["Timestamp", "Entity", "Entity ID", "Action", "Actor", "Details"].map((h) => <th key={h} className="text-left p-3 font-semibold text-slate-600">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {auditLog.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-100 hover:bg-app">
                      <td className="p-3 text-xs font-mono text-slate-500">{entry.timestamp?.slice(0, 19).replace("T", " ")}</td>
                      <td className="p-3"><span className="text-xs bg-slate-100 px-2 py-0.5">{entry.entity_type}</span></td>
                      <td className="p-3 text-xs font-mono text-slate-500">{entry.entity_id}</td>
                      <td className="p-3 text-xs">{entry.action}</td>
                      <td className="p-3 text-xs text-slate-500">{entry.actor?.slice(0, 16)}</td>
                      <td className="p-3 text-xs text-slate-500 max-w-xs truncate">{entry.details || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">
            {dialogType === "vendor" ? (editId ? "Edit Vendor" : "Add Vendor") :
             dialogType === "rfp" ? (editId ? "Edit RFP" : "New RFP") :
             dialogType === "bid" ? "Submit Bid" : editId ? "Edit Contract" : "Add Contract"}
          </DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {dialogType === "vendor" && (
              <>
                <Input placeholder="Company Name" value={vendorForm.company_name} onChange={(e) => setVendorForm((f) => ({ ...f, company_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Tax / Registration Number" value={vendorForm.tax_number} onChange={(e) => setVendorForm((f) => ({ ...f, tax_number: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Contact Person" value={vendorForm.contact_person} onChange={(e) => setVendorForm((f) => ({ ...f, contact_person: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Email" value={vendorForm.email} onChange={(e) => setVendorForm((f) => ({ ...f, email: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Performance Rating (1-5)</label>
                    <Select value={vendorForm.performance_rating} onValueChange={(v) => setVendorForm((f) => ({ ...f, performance_rating: v }))}>
                      <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-300 rounded-none">
                        {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={n.toString()}>{n}/5</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input placeholder="Risk Flags (optional)" value={vendorForm.risk_flags} onChange={(e) => setVendorForm((f) => ({ ...f, risk_flags: e.target.value }))} className="rounded-none bg-app border-slate-300" />
              </>
            )}
            {dialogType === "rfp" && (
              <>
                <Input placeholder="Requisitioner Name" value={rfpForm.requisitioner_name} onChange={(e) => setRfpForm((f) => ({ ...f, requisitioner_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Appointment Number (AP-XXXXX)" value={rfpForm.requisitioner_appointment} onChange={(e) => setRfpForm((f) => ({ ...f, requisitioner_appointment: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <textarea placeholder="Description of goods/services" value={rfpForm.description} onChange={(e) => setRfpForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />
                <Input placeholder="Estimated Value (ZAR)" type="number" value={rfpForm.estimated_value} onChange={(e) => setRfpForm((f) => ({ ...f, estimated_value: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Select value={rfpForm.status} onValueChange={(v) => setRfpForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none">
                    {["Draft", "Open for Bids", "Awarded", "Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </>
            )}
            {dialogType === "bid" && (
              <>
                <Select value={bidForm.rfp_id} onValueChange={(v) => setBidForm((f) => ({ ...f, rfp_id: v }))}>
                  <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Select RFP" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none">
                    {rfps.filter((r) => r.status === "Open for Bids").map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.id} - {r.description?.slice(0, 40)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input placeholder="Vendor Name" value={bidForm.vendor_name} onChange={(e) => setBidForm((f) => ({ ...f, vendor_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Bid Amount (ZAR)" type="number" value={bidForm.bid_amount} onChange={(e) => setBidForm((f) => ({ ...f, bid_amount: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Compliance docs (comma-separated)" value={bidForm.compliance_docs} onChange={(e) => setBidForm((f) => ({ ...f, compliance_docs: e.target.value }))} className="rounded-none bg-app border-slate-300" />
              </>
            )}
            {dialogType === "contract" && (
              <>
                <Input placeholder="Linked RFP ID" value={contractForm.rfp_id} onChange={(e) => setContractForm((f) => ({ ...f, rfp_id: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <Input placeholder="Vendor Name" value={contractForm.vendor_name} onChange={(e) => setContractForm((f) => ({ ...f, vendor_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-500 block mb-1">Start Date</label><Input type="date" value={contractForm.start_date} onChange={(e) => setContractForm((f) => ({ ...f, start_date: e.target.value }))} className="rounded-none bg-app border-slate-300" /></div>
                  <div><label className="text-xs text-slate-500 block mb-1">End Date</label><Input type="date" value={contractForm.end_date} onChange={(e) => setContractForm((f) => ({ ...f, end_date: e.target.value }))} className="rounded-none bg-app border-slate-300" /></div>
                </div>
                <Input placeholder="Total Value (ZAR)" type="number" value={contractForm.total_value} onChange={(e) => setContractForm((f) => ({ ...f, total_value: e.target.value }))} className="rounded-none bg-app border-slate-300" />
                <textarea placeholder="Deliverables" value={contractForm.deliverables} onChange={(e) => setContractForm((f) => ({ ...f, deliverables: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />
                <Select value={contractForm.compliance_status} onValueChange={(v) => setContractForm((f) => ({ ...f, compliance_status: v }))}>
                  <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-300 rounded-none">
                    {["Active", "Expired", "Breached"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </>
            )}
            <Button onClick={handleSave} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Save</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Award Confirmation */}
      <AlertDialog open={!!awardRfpId} onOpenChange={(o) => !o && setAwardRfpId(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader><AlertDialogTitle>Award Contract?</AlertDialogTitle>
            <AlertDialogDescription>This will create a contract from the winning bid and close the RFP.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAward} className="rounded-none bg-green-600 hover:bg-green-500"><Award className="w-4 h-4 mr-1" /> Award</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
